/* eslint-disable react-hooks/exhaustive-deps */
/** @jsx jsx */
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Flex,
  Grid,
  Container,
  Heading,
  useColorMode,
  jsx,
  useThemeUI,
} from 'theme-ui'
import { useEffect, useState } from 'react'
import { useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import { navigate } from 'gatsby'
import Text from '~/components/IntlComponents/Text'
import ArticleList from '~/components/Article/List'
import GooderCircle from '~/assets/images/gooder_circle.svg'
import ArticleFilterBar from '~/components/Article/FilterBar'
import Plus from '~/assets/images/plus.svg'
import ArticleFilterDropdown from '~/components/Article/FilterDropdown'
import ArticleSearch from '~/components/Article/Search'
import { WigglySectionDivider } from '~/components/Generic/SectionDivider/WigglySectionDivider'
import useMatchMedia from '~/hooks/utils/use-match-media'
import { toURL } from '~/utils/intl/localePrefix'

const ArticleSection = ({ title, description, articles }) => {
  const [, setColorMode] = useColorMode('Blog-Pink')

  // Get a list of all unique filters from the articles
  const filters = [
    ...new Set(
      articles.reduce((acc, cur) => {
        return cur.tags
          ? [...acc, ...cur.tags.map(tag => tag.toLowerCase())]
          : acc
      }, []),
    ),
  ]

  useEffect(() => {
    setColorMode('Blog-Pink')
    if (typeof window !== 'undefined') {
      const query = window?.location?.search?.substring(1).split('-').join(' ')
      if (filters.includes(query)) {
        setActiveFilter(query)
      } else if (query) {
        navigate(toURL('/blog'))
      }
    }
  }, [])

  const { theme } = useThemeUI()

  const mobile = useMatchMedia('(max-width: 767px)')

  const [small, setSmall] = useState(false)

  const translate = useTranslate()

  useEffect(() => {
    setSmall(mobile)
  }, [mobile])

  const [activeFilter, setActiveFilter] = useState(null)
  const [filteredArticles, setFilteredArticles] = useState(articles)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState(null)

  useEffect(() => {
    // Reset "Load More" amount when new filter is selected
    setPage(0)
    updateFilteredArticles()
  }, [activeFilter, search])

  const updateFilteredArticles = () => {
    let filtered = articles
    if (activeFilter) {
      filtered = articles.filter(article => {
        const lowercaseTags = article.tags
          ? article.tags.map(tag => tag.toLowerCase())
          : []
        return lowercaseTags.includes(activeFilter?.toLowerCase())
      })
    }
    if (search) {
      filtered = filtered.filter(
        article =>
          article.title.toLowerCase().includes(search.toLowerCase()) ||
          article.description?.childMarkdownRemark?.html
            ?.toLowerCase()
            .includes(search.toLowerCase()),
      )
    }
    setFilteredArticles(filtered)
  }

  return (
    <Container
      variant="fullWidth"
      sx={{
        flexDirection: 'column',
        background: [
          'linear-gradient(180deg, #F9C600 18%, #FF92DD 101.93%)',
          'linear-gradient(180deg, #F9C600 20%, #FF92DD 101.93%)',
          'linear-gradient(180deg, #F9C600 32%, #FF92DD 101.93%)',
        ],
        pb: '100px',
        overflowX: 'hidden',
      }}
    >
      <Box
        sx={{
          marginBottom: ['2vw', '168px'],
          color: 'waxFlower',
          position: 'relative',
          zIndex: 0,
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            py: '100px',
            px: '30px',
            backgroundColor: 'brilliantRose',
          }}
        >
          <Heading
            as="h1"
            variant="text.h1"
            sx={{
              width: ['100%', '90%'],
              textAlign: 'center',
              zIndex: 1,
            }}
          >
            {title}
          </Heading>
          {description && (
            <Text
              sx={{
                variant: 'text.body',
                textAlign: 'center',
                maxWidth: '720px',
                m: '0 auto',
                mt: [4, '15px'],
                p: {
                  m: 0,
                },
                zIndex: 1,
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: description.childMarkdownRemark.html,
                }}
              />
            </Text>
          )}
        </Flex>
        <Grid
          sx={{
            mb: ['118px', 0],
            position: 'relative',
          }}
        >
          <WigglySectionDivider
            small={small}
            topColor={theme.colors.brilliantRose}
            bottomColor={theme.colors.supernova}
          />
        </Grid>
      </Box>
      <Flex
        sx={{
          display: ['none', null, 'flex'],
          justifyContent: 'center',
          alignItems: 'center',
          mb: '60px',
        }}
      >
        <ArticleFilterBar
          filters={filters}
          activeFilter={activeFilter}
          setFilter={setActiveFilter}
        />
        <ArticleSearch setSearch={setSearch} />
      </Flex>
      <Box sx={{ position: 'relative', variant: 'responsive.mobileTablet' }}>
        <GooderCircle
          sx={{
            position: 'absolute',
            top: '38px',
            right: '-19px',
            transform: 'translate(0, -100%)',
            width: ['156px', null, 'auto'],
            height: 'auto',
            variant: 'responsive.mobileTablet',
          }}
        />
        <ArticleFilterDropdown
          filters={filters}
          activeFilter={activeFilter}
          setFilter={setActiveFilter}
        />
        <ArticleSearch setSearch={setSearch} />
      </Box>
      <Box
        sx={{
          position: 'relative',
          width: ['84%', '75%'],
          m: '0 auto',
          maxWidth: '1080px',
        }}
      >
        <GooderCircle
          sx={{
            '--size': 'clamp(150px, 80px + 7.5vw ,200px)',
            position: 'absolute',
            top: -310,
            right: -130,
            width: 'var(--size)',
            height: 'var(--size)',
            variant: 'responsive.desktop',
          }}
        />
        {/* Use the "page" variable to decide how many articles should be displayed */}
        <ArticleList
          search={search}
          articles={filteredArticles.slice(0, 10 + page * 7)}
          forwardSx={{ gridAutoFlow: 'dense' }}
        />
        {/* Only show "Load More" button if the number of displayed articles is less than the total amount of filtered articles */}
        {10 + page * 7 < filteredArticles.length && (
          <Button
            onClick={() => setPage(page + 1)}
            sx={{
              fontSize: 3,
              fontWeight: 'heading',
              color: 'blazeOrange',
              backgroundColor: 'waxFlower',
              width: 'auto',
              height: 'auto',
              padding: '15px 37px',
              borderRadius: '32px',
              display: 'block',
              margin: '0 auto',
              marginTop: '60px',
              ':hover': {
                backgroundColor: 'waxFlower',
              },
            }}
          >
            {translate('gallery.load_more')} <Plus sx={{ ml: '5px' }} />
          </Button>
        )}
      </Box>
    </Container>
  )
}

ArticleSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.shape({
    description: PropTypes.string,
  }),
  articles: PropTypes.arrayOf(PropTypes.shape({})),
}

export default ArticleSection
